'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@workspace/ui/lib/utils';
import { SocialTab, SocialTabs } from './components/SocialTabs';
import { useRouter, useSearchParams } from 'next/navigation';
import { ISocialPost } from 'types/src/types';
import { PostItem } from './components/PostItem';
import Link from 'next/link';
import { useProfile } from '@/src/hooks/use-profile';
import { Button } from '@workspace/ui/components/button';
import { Modal } from './components/Modal';
import { useBoolean } from '@/src/hooks/use-boolean';
import {
  useDiscoverPosts,
  useFollowingPosts,
} from '@/src/hooks/use-fetch-posts';
import { GameBlock } from './components/GameBlock';
import { useGameBySlug } from '@/src/hooks/use-game-by-slug';
import { Icon } from '@/src/components/ui';
import PageLoader from '@/src/components/ui/PageLoader';
import { useResponsive } from '@/src/hooks/use-responsive';
import { PostForm } from './components/PostForm';

export const HomeSocial = ({
  initialPosts,
}: {
  initialPosts: ISocialPost[];
}) => {
  const searchParams = useSearchParams();
  const gameSlug = searchParams.get('game') ?? '';
  const activeTab = (searchParams.get('tab') as SocialTab) ?? 'discover';
  const searchText = searchParams.get('search') ?? '';
  const hasGameFilter = !!gameSlug;
  const isMobile = useResponsive('down', 'md');
  const isDiscover = activeTab === 'discover';
  const isFollowing = activeTab === 'following';

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const discover = useDiscoverPosts(
    isDiscover,
    searchText,
    hasGameFilter ? gameSlug : undefined,
    initialPosts
  );
  const following = useFollowingPosts(
    isFollowing,
    searchText,
    hasGameFilter ? gameSlug : undefined
  );

  const data = isDiscover ? discover.data : following.data;
  const isLoading = isDiscover ? discover.isLoading : following.isLoading;

  const queryPosts = data?.pages?.flatMap((p) => p.posts ?? []) ?? [];
  const postsToRender = queryPosts;
  const { data: game } = useGameBySlug(gameSlug, hasGameFilter);

  const router = useRouter();
  const { data: profile } = useProfile();
  const isAnonymous = profile?.type === 'anonymous';
  const isOpenModal = useBoolean(false);

  const setTab = (tab: SocialTab) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('tab', tab);
    router.replace(`?${params.toString()}`);
  };

  const showGameEmptyState =
    hasGameFilter && !isLoading && postsToRender.length === 0;

  return (
    <div className="md:w-[674px] w-full flex flex-col items-center mx-auto h-full">
      {game ? (
        <Link
          href="/"
          className={cn(
            'w-full md:mt-5 md:pb-3 pb-2 flex items-center gap-2 hover:text-blue-primary transition-colors duration-300 px-6 md:px-0',
            isScrolled && 'border-b',
          )}
        >
          <Icon name="backPost" className="size-5 " />
          Back to social feed
        </Link>
      ) : (
        <SocialTabs
          activeTab={activeTab}
          onChange={setTab}
          isAnonymous={false}
        />
      )}
      {isLoading && <PageLoader />}
      <div
        ref={scrollRef}
        onScroll={(e) => {
          const top = (e.currentTarget as HTMLDivElement).scrollTop;
          setIsScrolled(top > 0);
        }}
        className="flex-1 w-full overflow-y-auto md:pb-4 pb-13 overflow-x-hidden no-scrollbar"
      >
        {game ? (
          <>
            <GameBlock
              imageUrl={game.coverImageUrl ?? ''}
              title={game.title ?? ''}
              description={game.description ?? ''}
              tags={game.tags ?? []}
              isIgdbGame={!!game.isIgdbGame}
            />
            <div
              className="md:flex hidden items-center justify-between w-full hover:cursor-pointer hover:bg-black/5 py-0.5 rounded-lg mt-3"
              onClick={() => {
                isAnonymous ? router.push('sign-in') : isOpenModal.onTrue();
              }}
            >
              <div className="flex items-center gap-4 text-[#5B6B78]">
                <img
                  src={profile?.avatarUrl || '/icons/logoHero.png'}
                  className="rounded-full size-[42px] object-contain bg-white"
                />
                {`Let’s talk about ${game.title}`}
              </div>
              <Button
                variant="secondary"
                type="button"
                className="px-5 h-8 rounded-xl text-xs!"
                disabled={isAnonymous}
                onClick={() => {
                  isAnonymous ? router.push('sign-in') : isOpenModal.onTrue();
                }}
              >
                Post
              </Button>
            </div>
            <hr className="w-full border-1 mt-3" />

            <SocialTabs
              activeTab={activeTab}
              onChange={setTab}
              isAnonymous={false}
            />
          </>
        ) : (
          <>
            <div
              className="md:flex hidden items-center justify-between w-full px-3 hover:cursor-pointer hover:bg-black/5 py-0.5 rounded-lg"
              onClick={() => {
                isAnonymous ? router.push('sign-in') : isOpenModal.onTrue();
              }}
            >
              <div className="flex items-center gap-4 text-[#5B6B78]">
                <img
                  src={profile?.avatarUrl || '/icons/logoHero.png'}
                  className="rounded-full size-[42px] object-contain bg-white"
                />
                What are you playing?
              </div>
              <Button
                variant="secondary"
                type="button"
                className="px-5 h-8 rounded-xl text-xs!"
                disabled={isAnonymous}
                onClick={() => {
                  isAnonymous ? router.push('sign-in') : isOpenModal.onTrue();
                }}
              >
                Post
              </Button>
            </div>
            <hr className="w-full md:border-0.5 md:mt-3" />
          </>
        )}
        {showGameEmptyState ? (
          <div className="flex flex-col items-center justify-center mt-10 text-center px-6">
            <p className="text-gray-800 text-lg font-semibold">
              No posts about this game yet.
            </p>
            <p className="text-gray-500 mt-2">
              Be the first to start the conversation.
            </p>
          </div>
        ) : postsToRender.length > 0 ? (
          <>
            {postsToRender.map((post) => {
              return post.repost ? (
                <div key={post.id} className="w-full">
                  <PostItem
                    post={post}
                    profile={profile}
                    repostedBy={post.repost.by.uniqueLink ?? null}
                    isMobile={isMobile}
                  />
                </div>
              ) : (
                <div key={post.id} className="w-full">
                  <PostItem post={post} profile={profile} isMobile={isMobile} />
                </div>
              );
            })}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center mt-20">
            <p className="text-gray-500 text-lg">No posts yet.</p>
          </div>
        )}
      </div>
      {isOpenModal.value && (
        <Modal open={isOpenModal.value} isPost>
          <PostForm
            isNotHome
            profile={profile}
            onCloseModal={isOpenModal.onFalse}
            game={game ? game : undefined}
          />
        </Modal>
      )}
    </div>
  );
};
